import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Command, CommandInput, CommandItem, CommandList } from '../Command';
import { DropdownMenuSeparator } from '../DropdownMenu';
import { FieldLabelRow } from '../Field';
import { Icon } from '../Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { selectTriggerClassName } from '../Select/SelectPrimitives';
import { Text } from '../Text';

type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectName =
  | { label: string; 'aria-label'?: string }
  | { label?: undefined /** Names the field when there is no visible label. */; 'aria-label': string };

type MultiSelectProps = MultiSelectName & {
  options: MultiSelectOption[];
  value: string[];
  onValueChange?: (values: string[]) => void;
  placeholder?: string;
  countNoun?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  isRequired?: boolean;
  tooltipText?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  labelTestId?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
};

type MenuSide = 'top' | 'bottom' | null;

function getMultiSelectLabel(
  options: MultiSelectOption[],
  value: string[],
  placeholder: string,
  countNoun: string,
): string {
  const checked = new Set(value);
  const labels = options.filter((option) => checked.has(option.value)).map((option) => option.label);
  if (labels.length === 0) return placeholder;
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.length} ${countNoun}`;
}

function matchesSearch(option: MultiSelectOption, search: string): boolean {
  return option.label.toLowerCase().includes(search.trim().toLowerCase());
}

/**
 * Multi-value picker for a fixed list the user recognizes by name, such as a status filter where several statuses
 * can apply at once. Set `searchable` when the list is long enough to filter. Use `Select` or `ComboBox` when exactly
 * one value applies.
 */
function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  countNoun = 'selected',
  disabled,
  className,
  triggerClassName,
  id: idProp,
  ref,
  label,
  isRequired,
  tooltipText,
  secondaryAction,
  labelTestId,
  searchable,
  searchPlaceholder = 'Search...',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
}: MultiSelectProps) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const nameId = `${id}-name`;
  const valueId = `${id}-value`;
  const requiredId = isRequired ? `${id}-required` : undefined;
  const countId = `${id}-count`;

  const [side, setSide] = React.useState<MenuSide>(null);
  const [open, setOpen] = React.useState(false);
  // Radix focuses the option under the pointer, so the ring follows the last input: keys show it, the mouse hides it.
  const [keyboardNav, setKeyboardNav] = React.useState(false);
  const showRing = () => setKeyboardNav(true);
  const [query, setQuery] = React.useState('');
  const [announcement, setAnnouncement] = React.useState('');
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const composedTriggerRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      triggerRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.RefObject<HTMLButtonElement | null>).current = el;
    },
    [ref],
  );
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const observerRef = React.useRef<MutationObserver | null>(null);

  const contentRefCallback = React.useCallback((el: HTMLDivElement | null) => {
    contentRef.current = el;
    observerRef.current?.disconnect();
    observerRef.current = null;
    setSide(null);
    if (!el) return;
    const applySide = () => {
      const placed = el.getAttribute('data-side');
      if (placed === 'top' || placed === 'bottom') setSide(placed);
    };
    applySide();
    observerRef.current = new MutationObserver(applySide);
    observerRef.current.observe(el, { attributes: true, attributeFilter: ['data-side'] });
  }, []);

  const checkedValues = new Set(value);
  const checkedCount = options.filter((option) => checkedValues.has(option.value)).length;
  const triggerText = getMultiSelectLabel(options, value, placeholder, countNoun);
  const effectiveSide = side ?? 'bottom';

  const toggle = (toggled: string, checked: boolean) => {
    const known = new Set(options.map((option) => option.value));
    const next = options
      .filter((option) => (option.value === toggled ? checked : checkedValues.has(option.value)))
      .map((option) => option.value);
    // Values with no matching option belong to the caller, so a toggle keeps them.
    onValueChange?.([...next, ...value.filter((v) => !known.has(v))]);
  };

  const matchCount = options.filter((option) => matchesSearch(option, query)).length;

  // Typing updates the polite status only after a pause, so screen readers hear the settled result count.
  React.useEffect(() => {
    if (!searchable || !open) return;
    const timer = setTimeout(() => {
      if (!query.trim()) setAnnouncement('');
      else
        setAnnouncement(matchCount === 0 ? 'No matches' : `${matchCount} ${matchCount === 1 ? 'option' : 'options'}`);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchable, open, query, matchCount]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery('');
      setAnnouncement('');
    }
  };

  const focusSearch = () => contentRef.current?.querySelector<HTMLInputElement>('[cmdk-input]')?.focus();

  const triggerProps = {
    ref: composedTriggerRef,
    id,
    'data-slot': 'multi-select',
    disabled,
    // Field name plus current value, so screen readers hear what is checked without opening the popup.
    'aria-labelledby': `${nameId} ${valueId}`,
    'aria-describedby': [requiredId, ariaDescribedBy].filter(Boolean).join(' ') || undefined,
    'aria-invalid': ariaInvalid,
    'data-testid': testId,
    className: cn(
      selectTriggerClassName,
      effectiveSide === 'bottom' && 'data-[state=open]:rounded-b-none',
      effectiveSide === 'top' && 'data-[state=open]:rounded-t-none data-[state=open]:rounded-b',
      triggerClassName,
    ),
  };

  const triggerChildren = (
    <>
      {!label && (
        <span id={nameId} className="sr-only">
          {ariaLabel}
        </span>
      )}
      <span id={valueId} data-slot="multi-select-value" className="min-w-0 flex-1 truncate text-left">
        {triggerText}
      </span>
      <Icon icon="chevronDown" className="size-4 opacity-50" />
      {/* aria-required is not allowed on a button, so the required state is its description. */}
      {isRequired && (
        <span id={requiredId} className="sr-only">
          Required
        </span>
      )}
    </>
  );

  const control = (
    // Non-modal: a modal menu aria-hides the page around its still-focusable trigger (axe aria-hidden-focus).
    <DropdownMenuPrimitive.Root modal={false}>
      <DropdownMenuPrimitive.Trigger {...triggerProps} onKeyDown={showRing}>
        {triggerChildren}
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={contentRefCallback}
          data-slot="multi-select-content"
          data-testid={testId ? `${testId}-content` : undefined}
          // A custom trigger id leaves Radix's default reference dangling, so the menu is named here.
          aria-labelledby={nameId}
          onKeyDown={showRing}
          onPointerMove={() => setKeyboardNav(false)}
          // Non-modal menus skip focus restore after an outside click; land back on the trigger unless focus moved on.
          onCloseAutoFocus={() => {
            if (!document.activeElement || document.activeElement === document.body) triggerRef.current?.focus();
          }}
          align="start"
          sideOffset={-1}
          className={cn(
            'bg-white text-grey-80 p-1 border shadow-md',
            // z-[150] clears the client's legacy Dialog stack (content up to z 140) and Sheet (z 100).
            'relative z-[150]',
            'w-(--radix-dropdown-menu-trigger-width) max-h-(--radix-dropdown-menu-content-available-height)',
            'origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto',
            'rounded-t-none rounded-b data-[side=top]:rounded-t data-[side=top]:rounded-b-none',
            'border-grey-30 data-[state=open]:border-blue-50',
            'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=closed]:animate-out',
            'motion-safe:data-[state=open]:fade-in-0 motion-safe:data-[state=closed]:fade-out-0',
            'motion-safe:data-[state=open]:zoom-in-95 motion-safe:data-[state=closed]:zoom-out-95',
            'motion-safe:data-[side=bottom]:slide-in-from-top-2 motion-safe:data-[side=top]:slide-in-from-bottom-2',
            'dark:bg-white/30',
            className,
          )}
        >
          {options.map((option) => (
            <DropdownMenuPrimitive.CheckboxItem
              key={option.value}
              data-slot="multi-select-option"
              data-testid={testId ? `${testId}-option-${option.value}` : undefined}
              textValue={option.label}
              checked={checkedValues.has(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
              onSelect={(event) => event.preventDefault()}
              className={cn(
                'group relative flex w-full cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-xs leading-[1.4] select-none',
                keyboardNav ? 'focus-ring' : 'outline-hidden',
                'data-highlighted:bg-grey-10',
                'data-disabled:pointer-events-none data-disabled:opacity-50',
              )}
            >
              {/* grey-60 keeps the unchecked border at 3:1 against white; grey-30 does not. */}
              <span
                aria-hidden="true"
                data-slot="multi-select-checkbox"
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-grey-60 bg-white text-primary-foreground',
                  'group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary',
                )}
              >
                <DropdownMenuPrimitive.ItemIndicator className="flex">
                  <Icon icon="luCheck" size={16} />
                </DropdownMenuPrimitive.ItemIndicator>
              </span>
              <Text className="min-w-0 flex-1 text-xs leading-[1.4] break-words">{option.label}</Text>
            </DropdownMenuPrimitive.CheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <div data-slot="multi-select-footer" className="flex flex-wrap items-center justify-between gap-2 pl-2">
            <span id={countId} data-slot="multi-select-count" className="text-xs text-grey-60">
              {checkedCount === 0 ? 'None selected' : `${checkedCount} selected`}
            </span>
            <DropdownMenuPrimitive.Item
              data-slot="multi-select-clear"
              data-testid={testId ? `${testId}-clear` : undefined}
              aria-describedby={countId}
              disabled={checkedCount === 0}
              onSelect={(event) => {
                event.preventDefault();
                onValueChange?.([]);
                // Clear disables itself, so keyboard focus moves to the first option rather than a dead item.
                contentRef.current?.querySelector<HTMLElement>('[role="menuitemcheckbox"]')?.focus();
              }}
              className={cn(
                'flex min-h-6 min-w-6 cursor-pointer items-center justify-center rounded-sm px-2 py-1 text-xs text-blue-50 select-none',
                keyboardNav ? 'focus-ring' : 'outline-hidden',
                // blue-50 drops under 4.5:1 on the grey-10 highlight.
                'data-highlighted:bg-grey-10 data-highlighted:text-blue-60',
                'data-disabled:pointer-events-none data-disabled:opacity-50',
              )}
            >
              Clear
            </DropdownMenuPrimitive.Item>
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );

  const searchControl = (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <button type="button" {...triggerProps}>
          {triggerChildren}
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRefCallback}
        data-slot="multi-select-content"
        data-testid={testId ? `${testId}-content` : undefined}
        aria-labelledby={nameId}
        align="start"
        sideOffset={-1}
        className={cn(
          'flex flex-col gap-0 overflow-hidden rounded-none border-blue-50 p-0 shadow-md',
          // z-[150] clears the client's legacy Dialog stack (content up to z 140) and Sheet (z 100).
          'z-[150] w-(--radix-popover-trigger-width)',
          // The cap keeps a long list scannable; only the options scroll, so the field and footer stay in view.
          'max-h-[min(var(--radix-popover-content-available-height),320px)]',
          effectiveSide === 'bottom' ? 'rounded-b' : 'rounded-t',
          className,
        )}
      >
        <Command
          // cmdk names its input through this label, overriding any aria-label on the input.
          label={searchPlaceholder}
          filter={(itemValue, search) => {
            const option = options.find((o) => o.value === itemValue);
            return option && matchesSearch(option, search) ? 1 : 0;
          }}
          className="min-h-0 flex-1"
        >
          <div className="relative shrink-0">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={searchPlaceholder}
              data-testid={testId ? `${testId}-search` : undefined}
              className={query ? 'pr-8' : undefined}
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                data-testid={testId ? `${testId}-search-clear` : undefined}
                onClick={() => {
                  setQuery('');
                  focusSearch();
                }}
                // cmdk's root handles Enter for the highlighted option, so the key must not bubble past this button.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.stopPropagation();
                }}
                className="absolute top-1/2 right-0.5 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-grey-60 hover:bg-grey-10 focus-ring"
              >
                <Icon icon="cross2" size={12} />
              </button>
            )}
          </div>
          <CommandList
            label={label ?? ariaLabel}
            // An empty listbox reads as a broken list, so it hides and the No matches message stands in.
            hidden={matchCount === 0}
            className="max-h-none min-h-0 flex-1 p-1"
          >
            {options.map((option) => {
              const checked = checkedValues.has(option.value);
              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  // cmdk spends aria-selected on the highlight, so the checked state rides on aria-checked.
                  aria-checked={checked}
                  data-state={checked ? 'checked' : 'unchecked'}
                  data-slot="multi-select-option"
                  data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                  onSelect={() => toggle(option.value, !checked)}
                  className="group h-auto cursor-pointer items-start rounded-sm py-1.5 leading-[1.4]"
                >
                  <span
                    aria-hidden="true"
                    data-slot="multi-select-checkbox"
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-grey-60 bg-white text-primary-foreground',
                      'group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary',
                    )}
                  >
                    {checked && <Icon icon="luCheck" size={16} className="text-primary-foreground" />}
                  </span>
                  <Text className="min-w-0 flex-1 text-xs leading-[1.4] break-words">{option.label}</Text>
                </CommandItem>
              );
            })}
          </CommandList>
          {matchCount === 0 && (
            <div data-slot="multi-select-empty" className="px-2 py-3 text-center text-xs text-grey-60">
              No matches
            </div>
          )}
        </Command>
        <div
          data-slot="multi-select-footer"
          className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-grey-30 py-1 pr-1 pl-3"
        >
          <span id={countId} data-slot="multi-select-count" className="text-xs text-grey-60">
            {checkedCount === 0 ? 'None selected' : `${checkedCount} selected`}
          </span>
          <button
            type="button"
            data-slot="multi-select-clear"
            data-testid={testId ? `${testId}-clear` : undefined}
            aria-describedby={countId}
            disabled={checkedCount === 0}
            onClick={() => {
              onValueChange?.([]);
              focusSearch();
            }}
            className={cn(
              'flex min-h-6 min-w-6 cursor-pointer items-center justify-center rounded-sm px-2 py-1 text-xs text-blue-50 focus-ring',
              // blue-50 drops under 4.5:1 on the grey-10 hover.
              'hover:bg-grey-10 hover:text-blue-60',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
          >
            Clear
          </button>
        </div>
        <output aria-live="polite" data-slot="multi-select-search-status" className="sr-only">
          {announcement}
        </output>
      </PopoverContent>
    </Popover>
  );

  const field = searchable ? searchControl : control;

  if (!label) return field;

  return (
    <div className="flex w-full flex-col gap-1 [&>*]:w-full">
      <FieldLabelRow
        id={nameId}
        htmlFor={id}
        isRequired={isRequired}
        tooltipText={tooltipText}
        secondaryAction={secondaryAction}
        tooltipTestId={testId ? `${testId}-tooltip` : undefined}
        data-testid={labelTestId}
      >
        {label}
      </FieldLabelRow>
      {field}
    </div>
  );
}

export { getMultiSelectLabel, MultiSelect };
export type { MultiSelectOption, MultiSelectProps };
